import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Bell, Plus, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationConfigs, accounts, libraries } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Table } from '@anyemp/ui-kit';
import NotificationConfigModal from '../components/NotificationConfig/NotificationConfigModal';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import eventTypes from '../../../shared/eventTypes.json';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import DataViewToggle from '../components/UI/DataViewToggle';
import PageHeader from '../components/UI/PageHeader';
import { RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';

const NOTIFICATION_EVENT_LABELS = Object.fromEntries(
  eventTypes.NOTIFICATION_EVENTS.map(e => [e.value, e.label])
);

const NotificationSettings = () => {
  const { hasPermission } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('notification-settings');

  const canCreate = hasPermission('talent-service', 'notification_config', 'create');
  const canUpdate = hasPermission('talent-service', 'notification_config', 'update');
  const canDelete = hasPermission('talent-service', 'notification_config', 'delete');

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationConfigs.getAll();
      setConfigs(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast.error('Failed to load notification configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleDelete = useCallback(async (id) => {
    setDeletingId(id);
    try {
      await notificationConfigs.remove(id);
      toast.success('Deleted');
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  }, [fetchConfigs]);

  const handleToggleActive = useCallback(async (config) => {
    try {
      await notificationConfigs.update(config.id, { is_active: !config.is_active });
      toast.success(config.is_active ? 'Disabled' : 'Enabled');
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  }, [fetchConfigs]);

  const rowKey = useCallback((row) => row.id, []);

  const columns = useMemo(() => [
    {
      key: 'event_type',
      label: 'Event',
      render: (row) => (
        <span className="font-medium text-light-text-primary dark:text-dark-text-primary">
          {NOTIFICATION_EVENT_LABELS[row.event_type] || row.event_type}
        </span>
      ),
    },
    {
      key: 'account_id',
      label: 'Account',
      render: (row) => (
        <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
          {row.account_name || row.account_login
            ? <>{row.account_name || row.account_login} <span className="text-light-text-tertiary dark:text-dark-text-tertiary">#{row.account_id}</span></>
            : `#${row.account_id}`}
        </span>
      ),
    },
    {
      key: 'credential_set_id',
      label: 'Credential Set',
      render: (row) => (
        <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
          {row.credential_set_name
            ? <>{row.credential_set_name} <span className="text-light-text-tertiary dark:text-dark-text-tertiary">#{row.credential_set_id}</span></>
            : `#${row.credential_set_id}`}
        </span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => (
        <button
          onClick={() => canUpdate && handleToggleActive(row)}
          className={`flex items-center gap-1.5 text-sm font-medium ${canUpdate ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
          title={canUpdate ? (row.is_active ? 'Click to disable' : 'Click to enable') : ''}
        >
          {row.is_active ? (
            <>
              <CheckCircle size={15} className="text-green-500" />
              <span className="text-green-600 dark:text-green-400">Active</span>
            </>
          ) : (
            <>
              <XCircle size={15} className="text-gray-400" />
              <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Disabled</span>
            </>
          )}
        </button>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-1 justify-end">
          {canUpdate && (
            <button
              onClick={() => { setEditingConfig(row); setModalOpen(true); }}
              className="inline-flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary transition-colors hover:text-violet-500 dark:text-dark-text-tertiary dark:hover:text-violet-400"
              title="Edit"
            >
              <Edit size={15} />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteTarget(row)}
              disabled={deletingId === row.id}
              className="inline-flex h-10 w-10 items-center justify-center rounded text-light-text-tertiary transition-colors hover:text-red-500 disabled:opacity-40 dark:text-dark-text-tertiary dark:hover:text-red-400"
              title="Delete"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ),
    },
  ], [canUpdate, canDelete, deletingId, handleToggleActive, handleDelete]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={<span className="inline-flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30"><Bell size={20} className="text-violet-600 dark:text-violet-400" /></span>Notification Settings</span>}
        description="Configure Discord notifications for talent events"
        actions={<>
          {canCreate && (
            <button onClick={() => { setEditingConfig(null); setModalOpen(true); }} className="flex min-h-11 items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-violet-700">
              <Plus size={16} /> Add Config
            </button>
          )}
          <DataViewToggle value={viewMode} onChange={setViewMode} />
        </>}
      />

      {viewMode === 'cards' && (
        loading ? <div className="py-12 text-center text-light-text-tertiary">Loading notification configs…</div> : configs.length ? (
          <ResponsiveCardGrid>
            {configs.map((row) => (
              <RecordCard key={row.id}>
                <RecordCardHeader
                  title={NOTIFICATION_EVENT_LABELS[row.event_type] || row.event_type}
                  badge={row.is_active ? <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">Active</span> : <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">Disabled</span>}
                  actions={columns.find((column) => column.key === 'actions')?.render(row)}
                />
                <RecordCardFields>
                  <RecordCardField label="Account">{row.account_name || row.account_login || `#${row.account_id}`}</RecordCardField>
                  <RecordCardField label="Credential set">{row.credential_set_name || `#${row.credential_set_id}`}</RecordCardField>
                  <RecordCardField label="Status">{columns.find((column) => column.key === 'is_active')?.render(row)}</RecordCardField>
                </RecordCardFields>
              </RecordCard>
            ))}
          </ResponsiveCardGrid>
        ) : <div className="py-12 text-center text-light-text-tertiary">No notification configs yet</div>
      )}
      {viewMode === 'table' && (
        <Table
          columns={columns}
          data={configs}
          isLoading={loading}
          rowKey={rowKey}
          emptyState={<div className="py-8 text-center text-light-text-tertiary dark:text-dark-text-tertiary">No notification configs yet</div>}
        />
      )}

      {/* Modal */}
      <NotificationConfigModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingConfig(null); }}
        config={editingConfig}
        existingConfigs={configs}
        onSaved={fetchConfigs}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget.id)}
        title="Delete Notification Config"
        message={<>Delete notification config for <strong>{NOTIFICATION_EVENT_LABELS[deleteTarget?.event_type] || deleteTarget?.event_type}</strong>?</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deletingId === deleteTarget?.id}
      />
    </div>
  );
};

export default NotificationSettings;
